import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  addRequiredResourceLoading,
  removeRequiredResourceLoading,
} from 'apps/web_forms/slices/formSlice';
import usePromise from 'apps/web_forms/hooks/usePromise';

import { newCachedRequest } from 'apps/web_forms/api/api';
import { addResource } from 'apps/web_forms/slices/resourceSlice';
import { getQualifiedResourceId } from 'apps/web_forms/utils/resourcesUtils';
import useGlobalSelector from 'apps/web_forms/hooks/useGlobalSelector';
import { ACCEPTED_TYPES_FOR_GENERATED_PARAM } from 'apps/web_forms/utils/constants';

// TODO: rip out entirely, if possible. We're already requesting all needed
// resources on page load, now, and it's unnecessarily slowing things down and
// making the resource code and a bunch of other stuff much more complicated
// and confusing.
// Replace w/ use of useResourceData
const useResourceRequest = ({
  resourceIdentifier,
  isRequired,
  startManually,
}) => {
  const [loadingResource, setLoadingResource] = useState(false);
  const [startRequestManually, setStartRequestManually] = useState(() => {});

  const { resources, resourceVersions } = useGlobalSelector();

  const { promise: resourceRequest, resolvePromise: resolveResourceRequest } =
    usePromise();

  const {
    promise: resourceListRequest,
    resolvePromise: resolveResourceListRequest,
  } = usePromise();

  const dispatch = useDispatch();

  useEffect(() => {
    if (resources?.length) resolveResourceListRequest(resources);
  }, [resources]);

  async function getCurrentResource() {
    const resourceList = await resourceListRequest;

    const fetchedResource = resourceList.find(
      (resource) => resource.identifier === resourceIdentifier
    );

    return fetchedResource || null;
  }

  useEffect(() => {
    async function newResourceRequest(overrideQualifiedResourceId) {
      setLoadingResource(true);

      const currentResource = await getCurrentResource();

      if (currentResource === null) {
        setLoadingResource(false);
        return null;
      }

      if (currentResource.identifier) {
        const versionToRetrieve = overrideQualifiedResourceId
          ? /version_(\d*)/gm.exec(overrideQualifiedResourceId)[1]
          : resourceVersions?.[currentResource.identifier];
        const backupQualifiedResourceId = getQualifiedResourceId(
          currentResource.identifier,
          versionToRetrieve
        );
        const qualifiedResourceId =
          overrideQualifiedResourceId || backupQualifiedResourceId;

        if (isRequired) dispatch(addRequiredResourceLoading());
        setLoadingResource(true);
        
        let isGeneratedRequired = ACCEPTED_TYPES_FOR_GENERATED_PARAM.includes(currentResource.mime_type) 

        const resourceData = await newCachedRequest(
          {
            qualifiedResourceId: qualifiedResourceId,
            fetchId: currentResource.id,
            optionalVersion: versionToRetrieve,
            generated: isGeneratedRequired,
          }
        );

        if (isRequired) dispatch(removeRequiredResourceLoading());
        setLoadingResource(false);
        dispatch(addResource(qualifiedResourceId, resourceData));

        resolveResourceRequest(resourceData);
        return resourceData;
      }

      return null;
    }

    if (resourceIdentifier) {
      if (startManually) {
        setStartRequestManually(() => newResourceRequest);
      } else {
        newResourceRequest();
      }
    }
  }, [resourceVersions]);

  return {
    loadingResource,
    resourceRequest,
    startRequest: startRequestManually,
  };
};

export default useResourceRequest;
